
import { defineComponent, inject } from 'vue'
import CustomerNoteTile from '@/components/elements/CustomerNoteTile.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { CustomerQuoteService } from '@/services/customerQuote.service'
import { CustomerQuoteDetails } from '@/@types/responses/customer_quote/CustomerQuoteDetails'
import LockingButton from '@/components/shared/LockingButton.vue'
import QuoteApprovalModal from '@/components/modals/QuoteApprovalModal.vue'
import DotsSvg from '@/components/svgs/DotsSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'MyQuote',
  components: {
    CustomerNoteTile,
    DotsSvg,
    LoadingPulse,
    LockingButton,
    NumericInput,
    QuoteApprovalModal,
    TextInput
  },
  beforeRouteLeave() {
    if (this.showQuoteApprovalModal) {
      this.showQuoteApprovalModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const customerQuoteService = inject(PROVIDER_CONSTANTS.customerQuoteService) as CustomerQuoteService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      customerQuoteService,
      stateService,
      toast
    }
  },
  data() {
    let quote: CustomerQuoteDetails|undefined

    return {
      loading: true,
      quote,
      saving: false,
      showMenu: false,
      showQuoteApprovalModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
    }
  },
  computed: {
    stateDisplayValue(): string|undefined {
      if (this.quote && this.quote.customer.stateId) {
        const stateDropdownOption = this.stateDropdownOptions.find(o => o.id == this.quote?.customer.stateId)
        return stateDropdownOption?.name
      }

      return undefined
    }
  },
  mounted() {
    this.getStates().then(() => {
      this.getQuote()
    })
  },
  methods: {
    closeMenu(): void {
      this.showMenu = false
    },
    getQuote(): Promise<void> {
      return this.customerQuoteService.getCustomerQuote(this.id).then(quote => {
        this.quote = quote
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to load your quote. Try reloading the page.')
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onApproveQuoteClicked(): void {
      this.showQuoteApprovalModal = true
    },
    onEditAccountInformationClicked(): void {
      this.$router.push('/my-account')
    },
    onQuoteApproved(): void {
      if (!this.quote) return

      this.saving = true

      this.customerQuoteService.updateCustomerQuote(this.id).then(() => {
        this.getQuote().then(() => {
          this.toast.success('Your quote was approved successfully.')
          this.saving = false
          this.showQuoteApprovalModal = false
        })
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to approve your quote. Please try again.')
      })
    },
    quotePartStatusText(status: number): string {
      switch(status) {
        case 1: return 'In Stock'
        case 2: return 'On Order'
        case 3: return 'Needs Ordered'
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.quote)
        document.title = `Quote #${this.quote.id} - Short Action Customs, LLC`
    }
  }
})
